/**
 * Calendar — wall-clock duration arithmetic for the appointment form's
 * Start/End pair.
 *
 * Deliberately TIMEZONE-FREE. The form's four fields are wall-clock strings the
 * user reads off the screen, and duration is what they reason about: picking
 * "1 hr" at 9:00 AM must display 10:00 AM on every day of the year. Converting
 * to an instant, adding 60 minutes and converting back would turn 1:30 AM into
 * 3:30 AM on the spring-forward morning — arithmetically defensible, and it
 * looks broken. So every function here is a pure `string -> string` transform
 * and none of them takes or reads a `tz`; `lib/tz.ts` remains the only place
 * wall-clock meets UTC, at submit time.
 *
 * `Date.UTC` appears below purely as a proleptic-Gregorian CALCULATOR — never as
 * an instant — which is what buys correct month/leap/year rollover for free.
 * That idiom is already established by `nextDayIso` in `./availability`.
 *
 * The preserved quantity is TOTAL wall-clock minutes across the whole pair, not
 * a time-of-day offset, which is why crossing midnight and preserving a
 * multi-day span both fall out with no special cases.
 */

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}$/;

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR;

/** A `YYYY-MM-DD` date paired with an `HH:mm` 24h time. */
export interface WallClock {
  date: string;
  time: string;
}

/** The Start/End field quartet as the form holds it. */
export interface StartEndFields {
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
}

export interface DurationPreset {
  /** Stable id — doubles as the `FilterChips` chip id. */
  id: string;
  label: string;
  minutes: number;
}

/**
 * The quick-select row. Immutable at the source; spread at the call site to
 * satisfy `FilterChipsProps.chips`, which is typed as a mutable array.
 */
export const DURATION_PRESETS: readonly DurationPreset[] = [
  { id: "15m", label: "15 min", minutes: 15 },
  { id: "30m", label: "30 min", minutes: 30 },
  { id: "45m", label: "45 min", minutes: 45 },
  { id: "1h", label: "1 hr", minutes: 60 },
  { id: "90m", label: "1.5 hr", minutes: 90 },
  { id: "2h", label: "2 hr", minutes: 120 },
];

/**
 * Total wall-clock minutes for a date+time pair, measured from a fixed
 * arbitrary origin — only differences between two of these are meaningful.
 *
 * Returns `null` for anything that isn't a complete, in-range pair. That null is
 * load-bearing: a native `<input type="date">` / `type="time"` reports `""`
 * while the user is still typing, and callers translate null into "leave the
 * other field alone" rather than guessing at a half-entered value.
 */
function toWallClockMinutes(date: string, time: string): number | null {
  if (!DATE_RE.test(date) || !TIME_RE.test(time)) return null;
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  // The regexes admit shapes the native pickers can't produce ("99:99"); range
  // the clock fields so a hand-crafted value can't shift End somewhere absurd.
  if (hh > 23 || mm > 59) return null;
  const ms = Date.UTC(y, m - 1, d, hh, mm);
  if (Number.isNaN(ms)) return null;
  return Math.round(ms / 60_000);
}

/** Inverse of {@link toWallClockMinutes}. */
function fromWallClockMinutes(totalMinutes: number): WallClock {
  const at = new Date(totalMinutes * 60_000);
  const pad = (n: number, width = 2) => String(n).padStart(width, "0");
  return {
    date: `${pad(at.getUTCFullYear(), 4)}-${pad(at.getUTCMonth() + 1)}-${pad(at.getUTCDate())}`,
    time: `${pad(at.getUTCHours())}:${pad(at.getUTCMinutes())}`,
  };
}

/**
 * `date`/`time` advanced by `minutes`, rolling the date across midnight, month,
 * year and leap boundaries. Negative offsets run backwards. `null` when the
 * input pair is incomplete or out of range.
 */
export function addMinutesToWallClock(
  date: string,
  time: string,
  minutes: number,
): WallClock | null {
  if (!Number.isFinite(minutes)) return null;
  const base = toWallClockMinutes(date, time);
  if (base === null) return null;
  return fromWallClockMinutes(base + Math.round(minutes));
}

/**
 * Elapsed wall-clock minutes from start to end. Negative when the end precedes
 * the start and zero for a degenerate pair — both are real states the form
 * allows, and both are reported faithfully rather than corrected here.
 */
export function wallClockDeltaMinutes(
  startDate: string,
  startTime: string,
  endDate: string,
  endTime: string,
): number | null {
  const from = toWallClockMinutes(startDate, startTime);
  const to = toWallClockMinutes(endDate, endTime);
  if (from === null || to === null) return null;
  return to - from;
}

/**
 * The End the form should adopt when the user moves Start, holding the current
 * duration exactly — the core of the feature. `null` means "leave End alone".
 *
 * Two distinct nulls, both deliberate:
 *  - the *next* Start doesn't parse (mid-typing) — nothing to anchor to;
 *  - the *current* pair doesn't parse (e.g. the user cleared End's time) —
 *    there is no duration to preserve, and writing into a field someone
 *    deliberately emptied would be its own surprise.
 *
 * An empty `endDate` is materialized to the start date instead of failing,
 * because an absent end date ALREADY means exactly that at both places the form
 * reads it (`AppointmentFormModal` `liveLookups` and `submit` both compute
 * `endDate || startDate`). That makes an existing implicit value explicit — the
 * asymmetry with an empty `endTime`, which carries no such meaning, is the
 * whole justification for treating the two differently.
 *
 * Whatever duration is currently in the fields is what survives: a hand-typed
 * 20 minutes stays 20 minutes. Nothing here ever snaps to a preset.
 */
export function shiftEndForStartChange(
  current: StartEndFields,
  next: { startDate: string; startTime: string },
): WallClock | null {
  const duration = wallClockDeltaMinutes(
    current.startDate,
    current.startTime,
    current.endDate || current.startDate,
    current.endTime,
  );
  if (duration === null) return null;
  return addMinutesToWallClock(next.startDate, next.startTime, duration);
}

/**
 * The preset id matching `minutes` exactly, or `""` for a custom duration.
 *
 * `""` is what makes the chip row show nothing selected: `FilterChips` compares
 * `chip.id === activeId`, so an id no chip carries leaves every chip inactive —
 * no sentinel chip and no fork of the shared component required.
 */
export function presetIdForMinutes(minutes: number | null): string {
  if (minutes === null) return "";
  return DURATION_PRESETS.find((p) => p.minutes === minutes)?.id ?? "";
}

/**
 * A compact human duration ("45m", "1h 20m", "2d 8h") for the readout beside
 * the chips. Empty string for zero, negative, or non-finite input — the caller
 * renders nothing in those cases, since a muted "-40m" reads as a rendering
 * bug and the submit-time error is the right channel for an inverted range.
 */
export function formatDurationLabel(minutes: number | null): string {
  if (minutes === null || !Number.isFinite(minutes) || minutes <= 0) return "";
  const whole = Math.round(minutes);
  const days = Math.floor(whole / MINUTES_PER_DAY);
  const hours = Math.floor((whole % MINUTES_PER_DAY) / MINUTES_PER_HOUR);
  const mins = whole % MINUTES_PER_HOUR;
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0) parts.push(`${mins}m`);
  return parts.join(" ");
}
